import { REGEX } from "constants/CommonConstants";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { createRoot } from "react-dom/client";

export const isEmail = (value) => REGEX.EMAIL_REGEX.test(value);
export const isMobile = (value) => REGEX.PHONE_NO_REGEX.test(value);

export const maskEmail = (email) => {
  if (!email) return "";
  const [name, domain] = email.split("@");
  if (!domain) return email;

  return `${name.slice(0, 2)}***@${domain}`;
};

export const maskMobile = (mobile) => {
  if (!mobile) return "";
  return `******${mobile.slice(-4)}`;
};

export const ISOToHumanReadableDate = (dateStr) => {
  const date = new Date(dateStr);

  let formatted = date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Uppercase only AM/PM, not month
  formatted = formatted.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());

  return formatted; // e.g., 18 Sep 2025 09:00 AM
};

export const ISOToHumanReadableWithTimeDate = (dateStr) => {
  const date = new Date(dateStr);

  let formatted = date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Uppercase only AM/PM, not month
  formatted = formatted.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());

  return formatted; // e.g., 18 Sep 2025 09:00 AM
};

export const authorizedUser = (res) => {
  if (res?.code === 440 || res?.status === 440) {
    localStorage.clear();
    window.location.href = `/auth/login?error=Your session has expired`;
  } else {
    return;
  }
};

export const renderStars = (value, showValue = true) => {
  if (value == null) return null;

  const stars = [1, 2, 3, 4, 5].map((star) => {
    let starClass = "fa-star";
    let colorClass = "star-light";

    if (star <= Math.floor(value)) {
      starClass = "fa-star";
      colorClass = "star-yellow";
    } else if (star - 0.5 <= value) {
      starClass = "fa-star-half-alt";
      colorClass = "star-yellow";
    }

    return <i key={star} className={`fa ${starClass} ${colorClass} mr-1`} />;
  });

  return (
    <span className="d-inline-flex align-items-center">
      {stars}
      {showValue && <span className="ml-1 text-muted">({value})</span>}
    </span>
  );
};

export const handleCreateSelectOption = ({
  inputValue,
  options,
  setOptions,
  onSelect,
  valueFormatter = (v) => v,
  labelFormatter = (v) => v,
}) => {
  const trimmedValue = inputValue.trim();
  if (!trimmedValue) return;
  const value = valueFormatter(trimmedValue);
  const label = labelFormatter(trimmedValue);
  // prevent duplicates (case-insensitive)
  const exists = options.some(
    (opt) => opt.value.toLowerCase() === value.toLowerCase(),
  );
  if (exists) {
    onSelect(value);
    return;
  }
  const newOption = { value, label };
  // add new to end
  setOptions((prev) => [...prev, newOption]);
  // auto-select new option
  onSelect(value);
};

export const showAlert = ({
  title = "",
  message = null, // JSX support
  type = "",
  confirmText = "Yes",
  cancelText = "Cancel",
  confirmStyle = "danger",
  cancelStyle = "secondary",
  showCancel = true,
  onConfirm = () => { },
  onCancel = () => { },
  children = null,
  closeOnConfirm = true,
  allowOutsideClick = true,
  showConfirm = true,
}) => {
  const container = document.createElement("div");
  const root = createRoot(container);
  document.body.appendChild(container);

  const handleClose = () => {
    root.unmount();
    container.remove();
  };

  const handleConfirm = async () => {
    const shouldClose = await onConfirm(handleClose);

    if (shouldClose !== false && closeOnConfirm) {
      handleClose();
    }
  };

  const handleCancel = () => {
    handleClose();
    onCancel();
  };

  root.render(
    <ReactBSAlert
      warning={type === "warning"}
      success={type === "success"}
      info={type === "info"}
      title={<p className="small-text">{title}</p>}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      confirmBtnBsStyle={confirmStyle}
      cancelBtnBsStyle={cancelStyle}
      confirmBtnText={confirmText}
      cancelBtnText={cancelText}
      showCancel={showCancel}
      showConfirm={showConfirm}
      allowOutsideClick={allowOutsideClick}
      btnSize=""
    >
      {message}
      {typeof children === "function" ? children(handleClose) : children}    </ReactBSAlert>,
  );
  return handleClose;
};

export const getPasswordStrengthColor = (passwordStrength) => {
  switch (passwordStrength) {
    case "Strong":
      return "text-success";
    case "Medium":
      return "text-warning";
    case "Weak":
    default:
      return "text-danger";
  }
};
export const getPasswordStrength = (password) => {
  let strength = "Weak";
  if (
    password.length >= 8 &&
    /\d/.test(password) &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password)
  ) {
    strength = "Strong";
  } else if (
    password.length >= 8 &&
    (/\d/.test(password) || /[@$!%*?&]/.test(password))
  ) {
    strength = "Medium";
  }
  return strength;
};

// Function to calculate duration between start and end time (12-hour format)
export const getDuration = (startTime, endTime) => {
  // Parse start and end times using regex
  const [startHour, startMin, startAM] = startTime
    .match(/(\d+):(\d+) (\w+)/)
    .slice(1);
  const [endHour, endMin, endAM] = endTime.match(/(\d+):(\d+) (\w+)/).slice(1);

  // Convert to 24-hour format
  let startH =
    Number(startHour) + (startAM === "PM" && startHour !== "12" ? 12 : 0);
  let endH = Number(endHour) + (endAM === "PM" && endHour !== "12" ? 12 : 0);

  // Calculate difference in hours and minutes
  let diffH = endH - startH;
  let diffM = Number(endMin) - Number(startMin);

  // Adjust if minutes are negative
  if (diffM < 0) {
    diffH -= 1;
    diffM += 60;
  }

  return `${diffH}h ${diffM}m`;
};

export const allowOnlyDigits = (e) => {
  if (
    !REGEX.DIGIT_KEY_REGEX.test(e.key) &&
    !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
  ) {
    e.preventDefault();
  }
};

export const toCamelCase = (str) => {
  if (!str) return "";

  return str
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const addOneYear = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  date.setFullYear(date.getFullYear() + 1);

  return date.toISOString().split("T")[0]; // YYYY-MM-DD
};

//converts 03 Jan 2026 to 2026-01-03
export const humanReadableToInputDate = (dateStr) => {
  if (!dateStr) return "";

  const [day, monthStr, year] = dateStr.split(" ");

  const months = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };

  const month = months[monthStr];

  return `${year}-${month}-${day.padStart(2, "0")}`;
};

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};